import React from 'react';
import styled from 'styled-components';
import { get } from 'lodash';
import { useSelector } from 'react-redux';
import {
  prepareParcelForRendering,
  prepareLicenseForRendering,
  prepareViolationsForRendering,
} from './selectors';
import { Meter, Bar } from '../../graphs';
import { HALF_CONTRAST } from '../../theme';
import { Button } from '../../ui';
import { Star } from '../../parcels';

import {
  Row,
  ColumnHalf,
  MapImg,
  HeaderRow,
  AddressLine1,
  Key,
  Value,
  Heading,
  GraphWrapper,
  ColumnHalfCentered,
  Subheader,
} from './ui';

import { GOOGLE_BASE_URL, GOOGLE_KEY } from './constants';

const iconStyle = {
  color: HALF_CONTRAST,
  width: 18,
  height: 16,
  paddingRight: 3,
  paddingLeft: 3,
};

const Link = styled.a``;
const BlockLine = styled.a`
  display: flex;
  justify-content: flex-end;
  text-decoration: none;
`;
const LinkDiv = styled.div`
  cursor: pointer;
  color: blue;
  text-decoration: underline;
`;

const NoopEl = styled.div``;

const renderRows = (data) =>
  data.map(({ label, value, Icon = NoopEl, link }) => (
    <Row key={label} style={{ alignItems: 'center' }}>
      {<Icon style={iconStyle} />}
      <Key>
        {label}
        {link ? (
          <Link href={link.href} target="_blank">
            {link.label}
          </Link>
        ) : null}
      </Key>
      <Value>
        {value && typeof value === 'object' ? (
          <LinkDiv
            onClick={value.onClick}
            onKeyPress={value.onClick}
            role="button"
            tabIndex="0"
          >
            {value.label}
          </LinkDiv>
        ) : (
          value
        )}
      </Value>
    </Row>
  ));

const mapState = ({
  parcels,
  selectedParcel,
  waterData,
  city,
  state,
  defaultZipcode,
}) => ({
  parcel: parcels[selectedParcel],
  city,
  state,
  defaultZipcode,
  waterData: (waterData[selectedParcel] || []).map(
    ({ quarter, year, gallons }) => ({
      label: `Q${quarter} ${year}`,
      'Water Usage': gallons,
    })
  ),
});

export const Basic = () => {
  const { parcel, waterData, city, state, defaultZipcode } = useSelector(
    mapState
  );

  if (!parcel) return null;

  const parcelData = prepareParcelForRendering(parcel);
  const licenseData = prepareLicenseForRendering(parcel);
  const violationsData = prepareViolationsForRendering(parcel);

  const latLng = get(parcel, 'yx', {});
  const size = '426x300';
  const id = get(parcel, 'parcel_id');

  return (
    <div>
      <Row>
        <ColumnHalf>
          <MapImg
            src={`${GOOGLE_BASE_URL}/streetview?size=${size}&location=${latLng}&pitch=-0.76&key=${GOOGLE_KEY}`}
            alt="Street View"
          />
        </ColumnHalf>
        <ColumnHalf>
          <HeaderRow>
            <ColumnHalf>
              <Row>
                <Star parcelId={id} />
                <AddressLine1>
                  {get(parcel, 'add_combo', get(parcel, 'address', ''))}
                </AddressLine1>
              </Row>
            </ColumnHalf>
            <ColumnHalf>
              <BlockLine
                target="_blank"
                rel="noopener noreferrer"
                href={`https://www.google.com/maps/dir//${get(
                  parcel,
                  'add_combo',
                  get(parcel, 'address', '')
                )
                  .split()
                  .join('+')},+${city},+${state}+${defaultZipcode}/`}
              >
                <Button active>Directions</Button>
              </BlockLine>
            </ColumnHalf>
          </HeaderRow>
          {renderRows(parcelData)}
        </ColumnHalf>
      </Row>
      <br />
      <Row>
        <ColumnHalf>
          <MapImg
            src={`${GOOGLE_BASE_URL}/staticmap?markers=color:red%7C${latLng}&size=${size}&center=${latLng}&zoom=18&maptype=hybrid&key=${GOOGLE_KEY}`}
            alt="Satillite View"
          />
        </ColumnHalf>
        <ColumnHalf>
          <Heading>Water use chart for the past year</Heading>
          <Subheader>Cubic feet per quarter</Subheader>
          <GraphWrapper>
            <Bar
              data={waterData}
              axisLabels={{
                x: 'Quarters',
                y: 'Cubic feet per quarter',
              }}
            />
          </GraphWrapper>
        </ColumnHalf>
      </Row>
      <br />
      <Row>
        <ColumnHalf>
          <Row>
            <ColumnHalfCentered>
              <Heading>Priority Score</Heading>
              <Meter value={parcel.priority} inverted total={10} />
            </ColumnHalfCentered>
            <ColumnHalfCentered>
              <Heading>Changeability Score</Heading>
              <Meter value={parcel.changeability} total={10} />
            </ColumnHalfCentered>
          </Row>
        </ColumnHalf>
        <ColumnHalf>
          <Row>
            <Heading>Licenses and permits</Heading>
          </Row>
          {renderRows(licenseData)}
        </ColumnHalf>
      </Row>
      <Heading>Violations, Enforcements and Calls for service</Heading>
      {renderRows(violationsData)}
    </div>
  );
};
